import React, { useEffect, useRef, useState } from "react"
import PaymentLogos from "../checkout/PaymentLogos"
import { HeartIcon, StarIcon, TruckIcon, GlobeAltIcon, BuildingStorefrontIcon } from "@heroicons/react/24/outline"
import { url as cmsUrl } from "aldoo-ra/CMS/url"
import { HeartIcon as HeartSolid } from "@heroicons/react/24/solid"
import useClientAuth from "../hooks/useClientAuth"
import { navigate } from "vike/client/router"
import { twMerge } from "tailwind-merge"
import Typography from "aldoo-ra/Typography"
import Img from "../ui/Img"
import Chip from "../ui/Chip"
import FavoriteButton from "../ui/FavoriteButton"
import ProductGallery from "../ui/ProductGallery"
import ProductPurchaseUI from "../ui/ProductPurchaseUI"
import { formatMoney } from "../account/ui"
import ProductRatingSummary from "../ui/ProductRatingSummary"
import ProductSharePanel from "../ui/ProductSharePanel"
import OrderTermsNote from "../ui/OrderTermsNote"
import StoreProductProperties from "../ui/StoreProductProperties"
import LookInsideButton from "../ui/LookInsideButton"
import { ProductBreadcrumb } from "../ui/Categories/CategoryBreadcrumb"
import Button from "aldoo-ra/Button"
import SeeAlsoHorizontal from "../ui/SeeAlsoHorizontal"
import MoreFromSeries from "../ui/MoreFromSeries"
import MoreByAuthor from "../ui/MoreByAuthor"
import AboutAuthor from "../ui/AboutAuthor"
import RecentlyViewed from "../ui/RecentlyViewed"
import ProductReviews from "../ui/Reviews/ProductReviews"
import LeaveReview, { ReviewSuccessfullyAdded } from "../ui/Reviews/LeaveReview"
import useT from "../hooks/useT"
import useSiteLanguage from "../hooks/useSiteLanguage"
import { useCategories, useContentTypes } from "../hooks/useCatalogData"
import { analyzeRatings } from "../utils/ratings"
import { presaleOf, presaleDate } from "../utils/presale"
import { basePrice, displayName, isOutOfStock, isPhysicalProduct, renderInstructions } from "../utils/productUtils"
import { buildContentUrl, authorUrl } from "../utils/contentRoutes"
import { useFavorite } from "../store/account"
import { requestSignIn } from "../utils/signIn"
import { ProductSelectionProvider, ExtensionSlot, extensionTabsOf, defaultBaseLine, useProductSelection } from "aldoo-ra/Store/extensions"
import PriceDisplay from "../ui/PriceDisplay"
import { useCurrency } from "../store/currency"
import { useFreeShipping } from "../ui/cart/freeShipping"
import { useCart } from "../store/cart"
import { activeDiscountPercent, applyDiscount } from "../utils/discountUtils"
import { expectedDelivery } from "../utils/delivery"

// a variant-group sibling's page: the same url a product card links to (by its profile, which it carries)
const siblingUrl = (sibling) => buildContentUrl(sibling)

// an outfit's part prices and its total (composition selectors): in the
// visitor's currency, the total less the product's own discount, as the buy
// button's price shows it
export function PartPrice({ product, amount, currency: from, total }) {
  const { currency, convert } = useCurrency()
  const shown = total ? applyDiscount(amount, activeDiscountPercent(product)) : amount
  return <PriceDisplay amount={convert(shown, from)} currency={currency} variant={total ? "medium" : "small"} />
}

// CMS description HTML: inline <script> tags do not run through
// dangerouslySetInnerHTML, so they are re-created after mount (with a
// DOMContentLoaded shim, the event has long fired) — same as the old site.
function HtmlContent({ html, className }) {
  const ref = useRef(null)
  useEffect(() => {
    if (!ref.current) return
    ref.current.querySelectorAll("script").forEach((old) => {
      const script = document.createElement("script")
      if (old.src) script.src = old.src
      else script.textContent = `;(function(){var _add=document.addEventListener;document.addEventListener=function(t,fn,o){if(t==='DOMContentLoaded')return fn();return _add.call(document,t,fn,o)};try{${old.textContent}}finally{document.addEventListener=_add;}})()`
      old.replaceWith(script)
    })
  }, [html])
  return <div ref={ref} className={className} dangerouslySetInnerHTML={{ __html: html }} />
}

/** An extension's tab, with the page's selection */
function ExtensionTab({ tab, product }) {
  const selection = useProductSelection(product)
  const Component = tab.component
  return (
    <Component
      product={product}
      selection={selection?.selection || {}}
      setSelection={selection?.setSelection || (() => {})}
      line={selection?.line || null}
      isComplete={selection?.isComplete ?? true}
    />
  )
}

// the page gutters:
const PAGE_PX = "px-4 tab:px-10 lg:px-[60px]"

function Tab({ active, onClick, accent = "primary", children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={twMerge("px-4 py-3 text-[11px] tab:text-[14px] font-semibold uppercase tracking-wide border-b-2 -mb-px transition-colors", active ? `${accent === "text" ? "border-text" : "border-primary"} text-text` : "border-transparent text-text_secondary hover:text-text")}
    >
      {children}
    </button>
  )
}

// The line the store product extensions price a selection from: the
// product's own price (cents, before the discount — the page applies it)
export const storeBaseLine = (product) => {
  const base = basePrice(product)
  return { ...defaultBaseLine(product), amount: base?.amount ?? null, currency: base?.currency ?? null, label: displayName(product) }
}

/**
 * /product/:pid — physical / license products: gallery, purchase card,
 * details / description / instructions tabs, see also, reviews, sticky buy bar.
 *
 * The store product extensions the product has (`product.extensions`) add
 * their slots — badges over the gallery, siblings and selectors above the buy
 * button, a row under the price, tabs — and share one selection: the buy
 * button waits until it is complete, the price is what it resolves to. A
 * product with none renders exactly the page it always did.
 */
/**
 * The seller's free delivery threshold beside the expected delivery, from what the cart already
 * holds from that seller (the cart's own free shipping offers, so it follows every change made in
 * the side cart or the checkout): reached already → "You already have free delivery from this
 * merchant"; this product would reach it → "Add this product for free delivery!"; else "Free
 * delivery over €25". Prices after discount, in the visitor's currency. Nothing when the seller has
 * no threshold or the product ships free anyway.
 */
function FreeDeliveryHint({ product }) {
  const t = useT()
  const { currency, convert } = useCurrency()
  const cart = useCart()
  const offers = useFreeShipping(cart) || []
  const from = product?.shipping?.freeShippingFrom
  if (!from || product.free_shipping) return null
  const threshold = convert(Number(from.amount) || 0, String(from.currency || "eur").toLowerCase())
  if (!threshold) return null
  const base = basePrice(product)
  const price = base ? applyDiscount(convert(base.amount, base.currency), activeDiscountPercent(product)) : 0
  // what is still missing with this seller: the cart's answer when the seller is in it, else the whole threshold
  const offer = offers.find((entry) => entry.sellerName && entry.sellerName === product.publisher)
  const remaining = offer ? convert(Number(offer.remaining) || 0, String(offer.currency || "eur").toLowerCase()) : threshold
  const bold = <b className="font-bold text-success">{t`Free delivery`}</b>
  let text
  if (offer?.met) text = <>{t`You already have`} {bold} {t`from this merchant`}</>
  else if (price >= remaining) text = <>{t`Add this product for`} {bold}!</>
  else text = <><b className="font-bold">{t`Free delivery`}</b> {t("over %amount%", formatMoney(threshold, currency))}</>
  return (
    <span className="text-[14px] text-text" data-testid="free-delivery-hint">
      {text}
    </span>
  )
}

export default function StoreProductPage(props) {
  return (
    <ProductSelectionProvider product={props.product} baseLine={storeBaseLine}>
      <StoreProductPageContent {...props} />
    </ProductSelectionProvider>
  )
}

function StoreProductPageContent({ pid, sub, product }) {
  const t = useT()
  const language = useSiteLanguage()
  const { auth } = useClientAuth()
  const { list: categories } = useCategories()
  const { list: contentTypes } = useContentTypes()
  const { isFavorite, toggle: toggleFavoriteState } = useFavorite(product)
  const [mainTab, setMainTab] = useState("details")
  const [instructionTab, setInstructionTab] = useState(0)
  const [sticky, setSticky] = useState(false)
  // the books the series row shows: the "More by" row leaves them out
  const [seriesPids, setSeriesPids] = useState(null)
  const purchaseRef = useRef(null)
  // the desktop card's buy block (its button at the end), and the breadcrumb that stays at the top over it
  const buyRef = useRef(null)
  const breadcrumbRef = useRef(null)
  const mobilePurchaseRef = useRef(null)
  const reviewsRef = useRef(null)
  const tabSwipeStartX = useRef(null)

  useEffect(() => {
    if (product?.description) setMainTab("description")
  }, [product?.pid, product?.description])

  useEffect(() => {
    // the bar slides in as soon as the card's buy button starts to go under what stays at the top — the
    // site's header and the product's breadcrumb — so a buy button is always in sight (the two may overlap
    // for a moment, never neither). On a phone it is there from the start: the cover fills the first
    // screen, and a visitor from an ad sees the price and the buy button without scrolling
    const phone = typeof window !== "undefined" && window.matchMedia ? window.matchMedia("(max-width: 767px)") : null
    const HEADER = 108
    const onScroll = () => {
      if (phone?.matches) return setSticky(true)
      const card = buyRef.current
      if (!card || window.getComputedStyle(purchaseRef.current || card).display === "none") return
      const button = [...card.querySelectorAll("button")].pop() || card
      const covered = Math.max(HEADER, breadcrumbRef.current?.getBoundingClientRect().bottom || 0)
      setSticky(button.getBoundingClientRect().top < covered)
    }
    window.addEventListener("scroll", onScroll, { passive: true })
    window.addEventListener("resize", onScroll)
    onScroll()
    return () => {
      window.removeEventListener("scroll", onScroll)
      window.removeEventListener("resize", onScroll)
    }
  }, [product?.pid])


  const name = displayName(product)
  const physical = isPhysicalProduct(product)
  const presale = presaleOf(product)
  const profile = product.profile || null
  // the profile as the content types list has it: its name in the site language, its field presets
  const contentType = (profile && contentTypes.find((ct) => ct.contentId === profile.id)) || null
  const contentTypeLabel = contentType?.name || profile?.name || null
  // the apparel accent: the picked size chip, selected tab and active thumbnail are marked in the text color, not the primary
  const accent = profile?.accent === "apparel" ? "text" : undefined
  const isLicenseKey = !!profile?.license
  const platforms = product.bookSettings?.platforms || []
  // the tabs the product's store extensions add (a size chart, say), after the page's own
  const extensionTabs = extensionTabsOf(product)
  // the description first — it is what a product is — then its details
  const visibleTabs = [...(product.description ? ["description"] : []), "details", ...(isLicenseKey && platforms.length > 0 ? ["instructions"] : []), ...(physical ? ["shipment"] : []), ...extensionTabs.map((tab) => `ext:${tab.id}`)]
  // the countries a physical product ships to, named in the page's language
  const countryNames = (codes) => {
    let names = null
    try {
      names = new Intl.DisplayNames([language], { type: "region" })
    } catch {}
    return codes.map((code) => ({ code, name: names?.of(code) || code }))
  }
  const { numRatings } = analyzeRatings(product.ratings)
  const credits = [
    { role: "author", label: t`Author` },
    { role: "illustrator", label: t`Illustrator` },
    { role: "translator", label: t`Translator` },
    { role: "narrator", label: t`Narrator` },
  ]
    .map((entry) => ({ ...entry, people: product[entry.role] || [] }))
    .filter((entry) => entry.people.length > 0)
  // the product's categories as chips: shown with the description and the details
  const productCategories = (product.category || []).map((id) => categories.find((c) => c.categoryId === id)).filter(Boolean)
  // what kind of thing it is, first: a chip to the type's own page (/type/<id>)
  const categoryChips = (contentTypeLabel || productCategories.length > 0) && (
    <div className="flex flex-row flex-wrap gap-2.5 mb-4" data-testid="product-categories">
      {contentTypeLabel && <Chip label={contentTypeLabel} to={`/type/${profile.id}`} data-testid="product-type-chip" />}
      {productCategories.map((category) => (
        <Chip key={category.categoryId} label={category.name} to={`/category/${category.categoryId}`} />
      ))}
    </div>
  )
  const images = (() => {
    // the picture as it came, in its own aspect, before the fitted square
    const picture = product.original || product.thumb || product.square
    // the product's own picture leads (it is what the cards and the variant
    // switcher show), then its other images; an import may keep it apart from them
    const screenshots = product.screenshots || []
    const base = picture && !screenshots.includes(picture) ? [picture, ...screenshots] : screenshots
    // its videos: the main one (its trailer) right after the picture, the others after the images; a product
    // from before the videos has its trailer only
    const listed = Array.isArray(product.videos) && product.videos.length ? product.videos : product.bookTrailer ? [{ url: product.bookTrailer, main: true }] : []
    if (!listed.length) return base
    const main = listed.find((video) => video.main) || listed[0]
    const others = listed.filter((video) => video !== main).map((video) => ({ type: "video", url: video.url }))
    const [first, ...rest] = base
    return first ? [first, { type: "video", url: main.url }, ...rest, ...others] : [{ type: "video", url: main.url }, ...others]
  })()

  const toggleFavourite = () => {
    if (!auth) return requestSignIn()
    toggleFavoriteState()
  }
  const scrollToReviews = () => reviewsRef.current?.scrollIntoView({ behavior: "smooth", block: "center" })
  // "Rate this product" opens the review dialog at once (the leave-review
  // sub-route); the page stays where it is
  const openReview = () => navigate(`${buildContentUrl(product)}/leave-review`, { keepScrollPosition: true })
  const scrollToPurchase = () => (purchaseRef.current || mobilePurchaseRef.current)?.scrollIntoView({ behavior: "smooth", block: "center" })

  const instructionsHtml = (entry) => renderInstructions(entry, language)

  const favoriteLabel = isFavorite ? t`Added to favorites` : t`Add to favorites`
  const thumb = (
    <Img src={product.square || product.thumb} alt={name} width={60} height={60} className="w-[36px] h-[36px] md:w-[60px] md:h-[60px] rounded overflow-hidden shrink-0" />
  )

  return (
    <div className="w-full">
      {/* Sticky purchase bar */}
      <div
        className={twMerge(
          "fixed left-0 right-0 z-[300] bg-paper shadow-[0_2px_8px_rgba(0,0,0,0.1)] py-3 transition-all duration-300",
          PAGE_PX,
          "md:top-[108px] bottom-0 md:bottom-auto",
          sticky ? "opacity-100 visible translate-y-0" : "opacity-0 invisible translate-y-4 md:-translate-y-4"
        )}
        // ends where the cart rail begins, when it is shown (Layout.css)
        data-fixed-bar
      >
        <div className="hidden md:flex items-center justify-between w-full gap-4">
          {thumb}
          <Typography 
            variant="body1_accent" 
            className="flex-1 truncate my-0"
          >
            {name}
          </Typography>
          <ProductPurchaseUI product={product} compact placement="product_page_sticky" />
          {/* add to favorites, beside the buy button */}
          <FavoriteButton product={product} className="w-[46px] h-[46px] rounded-lg border-2 border-text text-text hover:text-primary hover:border-primary" iconClassName="w-6 h-6" />
        </div>

        <div className="flex md:hidden flex-col gap-2 w-full">
          <div className="flex items-center gap-2">
            {thumb}
            <Typography 
              variant="txt1_accent" 
              className="truncate min-w-0"
            >
              {name}
            </Typography>
            <div className="flex-1" />
            <ProductPurchaseUI product={product} compact hideButton placement="product_page_sticky" />
          </div>

          {/* the heart at the bottom left, the buy button beside it */}
          <div className="flex items-center gap-2">
            <FavoriteButton product={product} className="w-[46px] h-[46px] rounded-lg border-2 border-text text-text" iconClassName="w-6 h-6" />
            <div className="flex-1 min-w-0">
              <ProductPurchaseUI product={product} hidePrice fullWidth compact placement="product_page_sticky" />
            </div>
          </div>
        </div>
      </div>

      {sub[0] === "leave-review" && sub[1] !== "success" && 
        <LeaveReview product={product} rootPath={buildContentUrl(product)} />
      }

      {sub[0] === "leave-review" && sub[1] === "success" &&       
        <ReviewSuccessfullyAdded rootPath={buildContentUrl(product)} />
      }

      {/* On desktop the breadcrumb and the pictures stay put while the text beside them scrolls — until the end of this block, where the description and details finish; then they scroll away with it */}
      <div className="relative">
        <div ref={breadcrumbRef} className={`w-full ${PAGE_PX} pt-4 md:pt-6 pb-0 md:pb-3 md:sticky md:top-[108px] z-20 bg-paper`} data-testid="product-breadcrumb">
          <ProductBreadcrumb product={product} itemName={name} />
        </div>

        {/* Main */}
        <div className={`relative w-full max-w-w1_xl flex flex-col md:flex-row items-start justify-between gap-2.5 tab:gap-10 md:gap-4 lg:gap-8 ${PAGE_PX} pt-5 md:pt-2 pb-4 md:pb-10 mx-auto`}>
          {/* Left column - Gallery*/}
          <div className="relative w-full md:w-[40%] lg:w-1/2 tab:max-w-[500px] xl:max-w-[550px] mx-auto md:mx-0 shrink-0 md:sticky md:top-[168px]" data-testid="product-gallery-column">
            {/* the extensions' labels (New, Outlet …) over the pictures */}
            <ExtensionSlot name="badges" product={product} className="absolute top-3 left-3 z-10 flex flex-wrap gap-2 pointer-events-none" />
            {/* the lightbox carries the purchase bar the old site had, so it needs the product, not only its pictures */}
            <ProductGallery images={images} productName={name} product={product} accent={accent} />
            {/* the sample pages, under the pictures (and their thumbnails) */}
            {product.preview && (
              <div className="flex justify-center mt-4">
                <LookInsideButton product={product} />
              </div>
            )}
          </div>

          {/* Right column - details */}
          <div className="w-full md:flex-1 tab:max-w-[500px] md:max-w-none mx-auto md:mx-0">
            <div className="border-2 md:border-0 border-black rounded-xl p-6 md:p-0 mb-10">
              <div className="flex flex-row justify-between items-start">
                <Typography 
                  component="h1" 
                  variant="h3" 
                  className="leading-none"
                >
                  {name}
                </Typography>

                <button 
                  type="button" 
                  onClick={toggleFavourite} 
                  className="md:hidden ml-4 p-1" 
                  aria-label={favoriteLabel}
                >
                  {isFavorite ? <HeartSolid className="w-6 h-6 text-primary" /> : <HeartIcon className="w-6 h-6" />}
                </button>
              </div>

              {/* who made it, right under the title — the details tab lists them too */}
              {credits.length > 0 && (
                <div className="flex flex-row flex-wrap gap-x-5 gap-y-1 mt-2 mb-4 text-sm md:text-base" data-testid="product-credits">
                  {credits.map(({ role, label, people }) => (
                    <span key={role} className="text-text_secondary">
                      {label.replace(/:\s*$/, "")}:{" "}
                      {people.map((person, i) => (
                        <React.Fragment key={person._id || person.slug || i}>
                          {i > 0 && ", "}
                          {role === "author" ? (
                            <Button 
                              variant="link"
                              href={authorUrl(person)}>
                                {person.name}
                              </Button>
                          ) : (
                            <span className="font-medium text-text">
                              {person.name}
                            </span>
                          )}
                        </React.Fragment>
                      ))}
                    </span>
                  ))}
                </div>
              )}
              {(contentTypeLabel || product.publisher) && (
                <div className={twMerge("flex flex-wrap items-center gap-x-3 gap-y-2", credits.length > 0 ? "" : "mt-3", product.stock > 0 && product.stock < 10 ? "mb-3" : "mb-6")}>
                  {/* the type by the title: a chip to the type's own page, as in the category row */}
                  {contentTypeLabel && <Chip tone="outline" size="sm" label={contentTypeLabel} to={`/type/${profile.id}`} data-testid="product-type-title" />}
                  {(product.deliverables?.length > 0 || isLicenseKey) && <Chip tone="outline" size="sm" label={t`Digital Product`} className="uppercase tracking-wide text-[11px]" />}
                  {/* whether it can be bought now — a thing that ships (a digital one never runs out; a pre-sale says so itself) */}
                  {physical && !presale && (
                    <Chip
                      tone={isOutOfStock(product) ? "error" : "success"}
                      size="sm"
                      label={isOutOfStock(product) ? t`Out of stock` : t`Available`}
                      className={isOutOfStock(product) ? "" : "bg-[#2e7d32] border-[#2e7d32]"}
                      data-testid="title-availability"
                    />
                  )}
                  {/* who sells it, as the details tab and the cart name it */}
                  {product.publisher && (
                    <span className="inline-flex items-center gap-1.5 text-[13px] text-text_secondary" data-testid="title-merchant">
                      <BuildingStorefrontIcon className="w-4 h-4 shrink-0" />
                      <span>
                        {t`Merchant`}:{" "}
                        {product.publisherSlug ? (
                          <a href={`/seller/${product.publisherSlug}`} className="text-text font-semibold underline-offset-2 hover:underline hover:text-primary" data-testid="title-merchant-link">
                            {t(product.publisher)}
                          </a>
                        ) : (
                          <span className="text-text font-semibold">{t(product.publisher)}</span>
                        )}
                      </span>
                    </span>
                  )}
                </div>
              )}
              {presale && (
                <div className="flex flex-wrap items-center gap-2 mb-6" data-testid="presale">
                  <Chip tone="info" size="sm" label={t`Pre-sale`} className="uppercase tracking-wide text-[11px]" />
                  {presale.releaseDate && <span className="text-[14px] text-text_secondary">{t("Out on %date%", presaleDate(presale.releaseDate, language))}</span>}
                </div>
              )}
              {!presale && product.stock > 0 && product.stock < 10 && (
                <div className="mb-6">
                  <Chip tone="warning" size="sm" label={t`Only a few left`} />
                </div>
              )}

              {/* Purchase card (desktop) */}
              <div ref={purchaseRef} className="hidden md:flex flex-col mt-6 soft_card bg-paper px-4 lg:px-7 py-7">
                {/* what the buyer picks before buying: the product's siblings (other colors), then its options */}
                <ExtensionSlot name="siblings" product={product} productUrl={siblingUrl} className="flex flex-col gap-3 mb-4" />
                <ExtensionSlot name="selectors" product={product} productUrl={siblingUrl} accent={accent} renderPrice={(price) => <PartPrice product={product} {...price} />} className="flex flex-col gap-4 mb-4" />
                <div className="mb-4" ref={buyRef}>
                  <ProductPurchaseUI product={product} placement="product_page" />
                </div>
                <ExtensionSlot name="belowPrice" product={product} className="flex flex-col gap-2 mb-4" />
                {isLicenseKey && platforms.length > 0 && (
                  <div className="flex flex-row flex-wrap items-center gap-3 mb-4 text-[13px] text-text_secondary font-medium">
                    {platforms.map((entry, i) => {
                      const p = entry.platform || entry
                      return (
                        <span key={i} className="inline-flex items-center gap-1.5">
                          {p.image && <img src={p.image} alt={p.name} className="h-[18px] w-auto object-contain" />}
                          {p.name}
                        </span>
                      )
                    })}
                  </div>
                )}
                {/* the rating at the left, the delivery at the right, on one row */}
                {(physical || numRatings > 0) && (
                  <div className="flex flex-row items-start justify-between gap-4 mb-4 text-[14px]">
                    <div className="flex flex-col gap-2">
                      {numRatings > 0 && <ProductRatingSummary product={product} onClickReviews={scrollToReviews} />}
                      {/* opposite the expected delivery: the seller's free delivery threshold */}
                      {physical && <FreeDeliveryHint product={product} />}
                    </div>
                    {physical && (
                      <div className="flex flex-col items-end">
                        <span className="inline-flex items-center gap-2 font-medium">
                          <TruckIcon className="w-5 h-5" />
                          {t`Expected delivery`}
                        </span>
                        <span>
                          {/* a pre-sale ships once it is out: by a few days after the release */}
                          {presale?.deliverBy ? t("By %date%", presaleDate(presale.deliverBy, language)) : expectedDelivery(product.deliveryDays ?? 3, language)}
                        </span>
                        {/* how this seller takes payment: online, cash on delivery (the seller's payment methods) */}
                        <PaymentLogos className="mt-2" online={product.shipping?.paymentMethods?.online !== false} cashOnDelivery={product.shipping?.paymentMethods?.cashOnDelivery !== false} />
                      </div>
                    )}
                  </div>
                )}

                {physical && product.free_shipping && (
                  <div className="mb-4">
                    <Chip tone="success" label={t`Free Shipping`} />
                  </div>
                )}

                <hr className="border-bordered mt-2" />
                <div className="flex flex-row items-center justify-between gap-2 py-3">
                  <button 
                    type="button" 
                    onClick={openReview} 
                    data-testid="rate-product" 
                    className="inline-flex items-center gap-1.5 text-sm font-medium hover:text-primary"
                  >
                    <StarIcon className="w-5 h-5" />
                    {t`Rate this product`}
                  </button>

                  <button 
                    type="button" 
                    onClick={toggleFavourite} 
                    data-testid="favorite-button" 
                    aria-pressed={isFavorite} 
                    className="inline-flex items-center gap-1.5 text-sm font-medium hover:text-primary"
                  >
                    {isFavorite 
                      ? <HeartSolid className="w-5 h-5 text-primary" /> 
                      : <HeartIcon className="w-5 h-5" />
                    }
                    {favoriteLabel}
                  </button>
                </div>
                <ProductSharePanel product={product} />
                <OrderTermsNote product={product} className="mt-4" />
              </div>

              {/* Mobile */}
              <div className="md:hidden">
                {/* the rating, and the way to add one — the desktop card's row, in the phone card */}
                <div className="flex flex-row flex-wrap items-center justify-between gap-x-4 gap-y-2 mb-4">
                  {numRatings > 0 && <ProductRatingSummary product={product} onClickReviews={scrollToReviews} />}

                  <button 
                    type="button" 
                    onClick={openReview} 
                    data-testid="rate-product-mobile" 
                    className="inline-flex items-center gap-2 text-[14px] font-medium hover:text-primary"
                  >
                    <StarIcon className="w-5 h-5" />
                    {t`Rate this product`}
                  </button>
                </div>

                <div ref={mobilePurchaseRef} className="flex flex-col items-center">
                  {isLicenseKey && platforms.length > 0 && (
                    <div className="flex flex-row flex-wrap items-center gap-3 mb-3 mr-auto text-[13px] text-text_secondary font-medium">
                      {platforms.map((entry, i) => {
                        const p = entry.platform || entry
                        return (
                          <span key={i} className="inline-flex items-center gap-1.5">
                            {p.image && <img src={p.image} alt={p.name} className="h-[18px] w-auto object-contain" />}
                            {p.name}
                          </span>
                        )
                      })}
                    </div>
                  )}
                  <ExtensionSlot name="siblings" product={product} productUrl={siblingUrl} className="flex flex-col gap-3 mb-4 w-full" />
                  <ExtensionSlot name="selectors" product={product} productUrl={siblingUrl} accent={accent} renderPrice={(price) => <PartPrice product={product} {...price} />} className="flex flex-col gap-4 mb-4 w-full" />
                  <ProductPurchaseUI product={product} fullWidth placement="product_page_mobile" />
                  <ExtensionSlot name="belowPrice" product={product} className="flex flex-col gap-2 mt-3 w-full" />
                </div>
                
                {physical && product.free_shipping && (
                  <div className="flex justify-center tab:justify-end mt-4">
                    <Chip tone="success" label={t`Free Shipping`} />
                  </div>
                )}
                {/* on a phone: under the buy button, as the desktop card has it under the share row */}
                <OrderTermsNote product={product} className="mt-4" />
              </div>
            </div>

            {/* Tabs (swipe left/right switches them on touch, as on the old site) */}
            <div
              onTouchStart={(e) => { tabSwipeStartX.current = e.touches[0].clientX }}
              onTouchEnd={(e) => {
                if (tabSwipeStartX.current === null) return
                const dx = e.changedTouches[0].clientX - tabSwipeStartX.current
                tabSwipeStartX.current = null
                if (Math.abs(dx) < 50) return
                const idx = visibleTabs.indexOf(mainTab)
                if (dx < 0 && idx < visibleTabs.length - 1) setMainTab(visibleTabs[idx + 1])
                else if (dx > 0 && idx > 0) setMainTab(visibleTabs[idx - 1])
              }}
            >
            <div className="flex border-b border-bordered mb-6">
              {product.description && <Tab accent={accent} active={mainTab === "description"} onClick={() => setMainTab("description")}>
                {t`Description`}
              </Tab>}

              <Tab accent={accent} active={mainTab === "details"} onClick={() => setMainTab("details")}>
                {t`Details`}
              </Tab>

              {isLicenseKey && platforms.length > 0 && <Tab accent={accent} active={mainTab === "instructions"} onClick={() => setMainTab("instructions")}>
                {t`Instructions`}
              </Tab>}

              {physical && <Tab accent={accent} active={mainTab === "shipment"} onClick={() => setMainTab("shipment")}>
                {t`Shipment`}
              </Tab>}

              {extensionTabs.map((tab) => (
                <Tab accent={accent} key={tab.id} active={mainTab === `ext:${tab.id}`} onClick={() => setMainTab(`ext:${tab.id}`)}>
                  {typeof tab.label === "function" ? tab.label(t) : t(tab.label)}
                </Tab>
              ))}
            </div>

            {extensionTabs.map((tab) => mainTab === `ext:${tab.id}` && <ExtensionTab key={tab.id} tab={tab} product={product} />)}

            {mainTab === "shipment" && (
              <div className="flex flex-col gap-5 text-[14px] md:text-[16px]" data-testid="shipment-tab">
                {/* the carrier first, then where it delivers */}
                <div className="flex flex-col gap-1">
                  <Typography variant="label_upper_dim">
                    {t`Shipping provider:`}
                  </Typography>

                  {/* every courier the seller ships this product with, each with its logo (CMS ShippingCarrier) */}
                  {product.shipping?.providers?.length ? (
                    <div className="flex flex-wrap items-center gap-x-6 gap-y-3" data-testid="shipment-providers">
                      {product.shipping.providers.map((provider) => {
                        const name = provider.carrier?.name || provider.name
                        // the logo alone, as tall as it reads well and as wide as its own shape;
                        // the name on hover (and for screen readers). No logo: the truck and the name.
                        return provider.carrier?.logo ? (
                          <img key={provider.providerId} src={cmsUrl(provider.carrier.logo)} alt={name} title={name} className="h-10 w-auto max-w-[140px] object-contain" />
                        ) : (
                          <div key={provider.providerId} className="inline-flex items-center gap-1">
                            <TruckIcon className="w-5 h-5" />
                            <Typography variant="txt1_accent">{name}</Typography>
                          </div>
                        )
                      })}
                    </div>
                  ) : (
                    <div className="inline-flex items-center gap-1">
                      <TruckIcon className="w-5 h-5" />
                      <Typography variant="txt1_accent">
                        {product.shipping?.provider?.name || "—"}
                      </Typography>
                    </div>
                  )}
                </div>

                <div className="flex flex-col gap-1">
                  <Typography variant="label_upper_dim">
                    {t`Delivery time:`}
                  </Typography>
                  <Typography variant="txt1_accent">
                    {presale?.deliverBy
                      ? t("Pre-sale: delivered by %date%, up to 3 days after the release", presaleDate(presale.deliverBy, language))
                      : t("%days% business days", product.deliveryDays ?? 3)}
                  </Typography>
                </div>

                {/* the seller's free shipping offer, as the cart says it */}
                {product.shipping?.freeShippingFrom && (
                  <div className="flex flex-col gap-1" data-testid="shipment-free-shipping">
                    <Typography variant="label_upper_dim">{t`Free shipping:`}</Typography>
                    <Typography variant="txt1_accent" className="text-success">
                      {t("On orders over %amount% from this merchant", formatMoney(product.shipping.freeShippingFrom.amount, product.shipping.freeShippingFrom.currency))}
                    </Typography>
                  </div>
                )}

                <div className="flex flex-col gap-1">
                  <Typography variant="label_upper_dim">
                    {t`Ships to:`}
                  </Typography>

                  {product.shipping?.mode === "countries" && product.shipping.countries?.length ? (
                    <div className="flex flex-wrap gap-2">
                      {countryNames(product.shipping.countries).map(({ code, name }) => (
                        <Chip key={code} tone="outline" size="sm" label={name} />
                      ))}
                    </div>
                  ) : (
                    <div className="inline-flex items-center gap-1 font-semibold">
                      <GlobeAltIcon className="w-5 h-5" />
                      <Typography variant="txt1_accent">
                        {t`Worldwide`}
                      </Typography>
                    </div>
                  )}
                </div>
              </div>
            )}
            {mainTab === "description" && (
              <>
                <HtmlContent html={product.description} className="sf-html text-sm md:text-base leading-relaxed" />

                {/* where the product is filed, under what it is as well */}
                {categoryChips && <div className="mt-6">{categoryChips}</div>}
              </>
            )}
            {/* in the page whichever tab is open (hidden until chosen): a crawler reads the publisher, pages and ISBN too */}
            <div hidden={mainTab !== "details"}>
              <StoreProductProperties product={product} profile={contentType} />
              {categoryChips}
            </div>
            {mainTab === "instructions" && (
              <>
                <div className="flex flex-row flex-wrap gap-2 mb-4">
                  {platforms.map((entry, i) => {
                    const p = entry.platform || entry
                    return (
                      <Chip key={i} tone={instructionTab === i ? "primary" : "outline"} size="sm" onClick={() => setInstructionTab(i)} icon={p.image ? <img src={p.image} alt="" className="h-4 w-auto" /> : null} label={p.name} />
                    )
                  })}
                </div>
                {platforms[instructionTab] && (
                  <div className="sf-html text-sm text-text_secondary leading-relaxed" dangerouslySetInnerHTML={{ __html: instructionsHtml(platforms[instructionTab]) }} />
                )}
              </>
            )}
            </div>
          </div>
        </div>
      </div>

      <div className={`w-full ${PAGE_PX} pt-[60px]`}>
        <AboutAuthor product={product} />
      </div>
      <div className={`w-full ${PAGE_PX} pt-[20px] pb-10`}>
        <MoreFromSeries product={product} onList={(pids) => setSeriesPids(new Set(pids))} />
      </div>
      <div className={`w-full ${PAGE_PX} pt-[20px]`}>
        <MoreByAuthor product={product} exclude={seriesPids} className="mb-[60px]" />
      </div>
      {/* the products the product links to by hand (related, cross-sell), before the computed ones; the reviews after them */}
      <ExtensionSlot name="recommendations" product={product} className={`w-full ${PAGE_PX} pt-10`} />
      <div className={`w-full ${PAGE_PX} pt-10 pb-10`}>
        <SeeAlsoHorizontal list={product.see_also} />
      </div>
      <div ref={reviewsRef} className={`w-full ${PAGE_PX}`}>
        <ProductReviews product={product} />
      </div>
      <div className={`w-full ${PAGE_PX} pb-20`}>
        <RecentlyViewed except={product.pid} />
      </div>
    </div>
  )
}
